import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Grid,
  Chip,
  Paper,
  Alert,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle,
  Cancel,
  Warning,
} from "@mui/icons-material";

const HistoricalTransactionsSection = ({ historicalTransactions }) => {
  if (!historicalTransactions || historicalTransactions.length === 0) {
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Historical Transactions</Typography>
          <Chip
            label="No History"
            size="small"
            color="default"
            sx={{ ml: 2 }}
          />
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info">
            No historical transactions found for this traveler.
          </Alert>
        </AccordionDetails>
      </Accordion>
    );
  }

  // Sort transactions by date (most recent first)
  const sortedTransactions = [...historicalTransactions].sort((a, b) => {
    const dateA = new Date(a.crossingDate || 0);
    const dateB = new Date(b.crossingDate || 0);
    return dateB - dateA;
  });

  const getDecisionColor = (decision) => {
    switch (decision) {
      case "ALLOW":
        return "success";
      case "DENY":
        return "error";
      case "REVIEW":
        return "warning";
      default:
        return "default";
    }
  };

  const getRiskColor = (risk) => {
    switch (risk?.toUpperCase()) {
      case "LOW":
        return "success";
      case "MEDIUM":
        return "warning";
      case "HIGH":
      case "CRITICAL":
        return "error";
      default:
        return "default";
    }
  };

  const getDecisionIcon = (decision, hasInspections) => {
    if (decision === "DENY") return <Cancel fontSize="small" />;
    if (hasInspections) return <Warning fontSize="small" />;
    if (decision === "ALLOW") return <CheckCircle fontSize="small" />;
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown Date";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Historical Transactions</Typography>
        <Chip
          label={`${sortedTransactions.length} Records`}
          size="small"
          color="primary"
          sx={{ ml: 2 }}
        />
      </AccordionSummary>
      <AccordionDetails>
        {/* Summary Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <Box
              sx={{
                textAlign: "center",
                p: 1,
                bgcolor: "primary.lighter",
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" color="primary.dark">
                {sortedTransactions.length}
              </Typography>
              <Typography variant="caption">Total Crossings</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                textAlign: "center",
                p: 1,
                bgcolor: "success.lighter",
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" color="success.dark">
                {
                  sortedTransactions.filter(
                    (t) => t.transaction?.personDecision === "ALLOW"
                  ).length
                }
              </Typography>
              <Typography variant="caption">Approved</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                textAlign: "center",
                p: 1,
                bgcolor: "error.lighter",
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" color="error.dark">
                {
                  sortedTransactions.filter(
                    (t) =>
                      t.transaction?.personDecision === "DENY" ||
                      t.inspectedCar ||
                      t.inspectedPhysicalPassport
                  ).length
                }
              </Typography>
              <Typography variant="caption">Issues</Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Transaction List */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Recent Transactions
          </Typography>
          {sortedTransactions.slice(0, 10).map((transaction, index) => {
            const decision = transaction.transaction?.personDecision;
            const hasInspections =
              transaction.inspectedCar || transaction.inspectedPhysicalPassport;

            return (
              <Paper
                key={transaction.transaction?.transactionUuid || index}
                variant="outlined"
                sx={{ p: 2, mb: 1 }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(transaction.crossingDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2">
                      {transaction.crossingPlace || "Unknown"}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {getDecisionIcon(decision, hasInspections)}
                      <Chip
                        label={decision || "Unknown"}
                        size="small"
                        color={getDecisionColor(decision)}
                        sx={{ ml: 0.5 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    {transaction.riskAssessmentResult && (
                      <Chip
                        label={transaction.riskAssessmentResult}
                        size="small"
                        color={getRiskColor(transaction.riskAssessmentResult)}
                      />
                    )}
                  </Grid>
                  <Grid item xs={2}>
                    {hasInspections && (
                      <Typography variant="caption" color="warning.dark">
                        {[
                          transaction.inspectedCar && "Car",
                          transaction.inspectedPhysicalPassport && "Passport",
                        ]
                          .filter(Boolean)
                          .join(", ")}{" "}
                        Inspected
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            );
          })}

          {sortedTransactions.length > 10 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Showing 10 most recent transactions out of{" "}
              {sortedTransactions.length} total.
            </Alert>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default HistoricalTransactionsSection;
